import { defineStore } from 'pinia';
import { openDB } from 'idb';
import apiService from '../api/apiService';
import { relationLegacyAdapter } from '../analysis/legacy';
import { normalizeRelationFileType, resolveImageMimeType } from './imageStoreShared';

const createImageUrlsState = () => ({
    original: null,
    processed: null
});

const createImageIdsState = () => ({
    original: null,
    processed: null
});

const createImageTypesState = () => ({
    original: null,
    processed: null
});

const createOriginalFileInfoState = () => ({
    format: '',
    name: ''
});

const withCorsCacheBust = (imageUrl) => {
    try {
        const url = new URL(imageUrl);
        url.searchParams.set('__cors_ts', String(Date.now()));
        return url.toString();
    } catch (_error) {
        const separator = String(imageUrl || '').includes('?') ? '&' : '?';
        return `${imageUrl}${separator}__cors_ts=${Date.now()}`;
    }
};

export const useImageAssetStore = defineStore('imageAsset', {
    state: () => ({
        imageData: null,
        processedImageData: null,
        fileObject: null,
        imageUrls: createImageUrlsState(),
        imageIds: createImageIdsState(),
        imageTypes: createImageTypesState(),
        originalFileInfo: createOriginalFileInfoState(),
        currentRelation: null,
        relationStatus: 'idle',
        relationError: null,
        db: null,
        _dbUnavailable: false,
        _dbInitPromise: null
    }),

    getters: {
        hasImageInput: (state) => Boolean(state.imageData)
    },

    actions: {
        async initDB() {
            if (this.db) {
                return this.db;
            }

            if (this._dbUnavailable) {
                throw new Error('[IndexedDB] disabled for current session after previous init failure');
            }

            if (this._dbInitPromise) {
                return this._dbInitPromise;
            }

            const timeoutMs = 4000;
            const openPromise = openDB('imageStore', 3, {
                upgrade(db, oldVersion) {
                    if (oldVersion === 0) {
                        db.createObjectStore('images', { keyPath: 'id' });
                        db.createObjectStore('referenceAnalysisCore', { keyPath: 'cacheKey' });
                        db.createObjectStore('referenceAnalysisEvidence', { keyPath: 'cacheKey' });
                    } else if (oldVersion === 1) {
                        if (!db.objectStoreNames.contains('images')) {
                            db.createObjectStore('images', { keyPath: 'id' });
                        }
                        if (!db.objectStoreNames.contains('referenceAnalysisCore')) {
                            db.createObjectStore('referenceAnalysisCore', { keyPath: 'cacheKey' });
                        }
                        if (!db.objectStoreNames.contains('referenceAnalysisEvidence')) {
                            db.createObjectStore('referenceAnalysisEvidence', { keyPath: 'cacheKey' });
                        }
                    } else if (oldVersion === 2) {
                        if (!db.objectStoreNames.contains('referenceAnalysisCore')) {
                            db.createObjectStore('referenceAnalysisCore', { keyPath: 'cacheKey' });
                        }
                        if (!db.objectStoreNames.contains('referenceAnalysisEvidence')) {
                            db.createObjectStore('referenceAnalysisEvidence', { keyPath: 'cacheKey' });
                        }
                    }
                },
                blocked() {
                    console.warn('[IndexedDB] openDB blocked: another tab/connection is preventing upgrade');
                },
                blocking() {
                    console.warn('[IndexedDB] openDB blocking: this connection blocks a future upgrade');
                },
                terminated() {
                    console.warn('[IndexedDB] connection terminated unexpectedly');
                }
            });

            this._dbInitPromise = Promise.race([
                openPromise,
                new Promise((_, reject) => {
                    setTimeout(() => {
                        reject(new Error(`[IndexedDB] timeout at initDB after ${timeoutMs}ms`));
                    }, timeoutMs);
                })
            ]).then((db) => {
                this.db = db;
                this._dbUnavailable = false;
                return db;
            }).catch((error) => {
                this._dbUnavailable = true;
                console.error('[IndexedDB] 初始化数据库失败:', error);
                throw error;
            }).finally(() => {
                this._dbInitPromise = null;
            });

            return this._dbInitPromise;
        },

        async setImage(data, imageId, imageType) {
            if (!data) {
                this.resetAssetState();
                return;
            }

            const safeImageType = imageType || 'image/jpeg';

            const awaitWithTimeout = (promise, timeoutMs, stage) => {
                return Promise.race([
                    promise,
                    new Promise((_, reject) => {
                        setTimeout(() => {
                            reject(new Error(`[IndexedDB] timeout at ${stage} after ${timeoutMs}ms`));
                        }, timeoutMs);
                    })
                ]);
            };

            this.imageData = data;
            this.processedImageData = null;
            this.imageIds.original = imageId;
            this.imageIds.processed = null;
            this.imageTypes.original = safeImageType;
            this.imageTypes.processed = null;
            this.imageUrls.processed = null;

            try {
                if (!this.db) {
                    await awaitWithTimeout(this.initDB(), 4000, 'initDB');
                }

                await awaitWithTimeout(this.db.put('images', {
                    id: imageId,
                    data,
                    type: safeImageType,
                    timestamp: Date.now()
                }), 4000, 'db.put(images)');
            } catch (error) {
                console.warn('[IndexedDB] save image failed:', error);
            }
        },

        async saveOriginalImageToServer() {
            if (!this.imageData || !this.imageIds.original || !this.imageTypes.original) {
                console.warn('[ImageAssetStore API] lack of image info:', {
                    imageData: this.imageData,
                    imageIds: this.imageIds,
                    imageTypes: this.imageTypes
                });
                return;
            }

            const hashId = this.imageIds.original;

            try {
                const relation = await this.bootstrapRelation(hashId, 'original');
                if (relation) {
                    this.imageUrls.original = relation.original_image.image_url;
                    this.imageTypes.original = `image/${relation.original_image.file_type}`;
                    this.currentRelation = relation;

                    await relationLegacyAdapter.touchRelation(hashId, { imageType: 'original' });
                    return relation;
                }
            } catch (relationError) {
                console.warn('[ImageAssetStore] bootstrapRelation失败，降级到原有逻辑:', relationError);

                try {
                    const uploadSource = this.fileObject instanceof File ? this.fileObject : null;
                    const fallbackResponse = await apiService.saveImageToServer(
                        hashId,
                        this.imageData,
                        this.imageTypes.original,
                        'original',
                        { uploadSource }
                    );
                    const fallbackPayload = fallbackResponse?.data?.detail || fallbackResponse?.data || {};
                    const fallbackImageUrl = fallbackResponse?.imageUrl
                        || fallbackPayload?.img_url
                        || fallbackPayload?.image_url
                        || this.imageUrls.original
                        || null;

                    const fallbackOriginalImage = fallbackPayload?.original_image || {
                        hash_id: hashId,
                        image_url: fallbackImageUrl,
                        file_type: normalizeRelationFileType(
                            fallbackPayload?.file_type
                            || fallbackResponse?.requestData?.file_type
                            || this.imageTypes.original
                        ),
                        image_type: fallbackPayload?.image_type
                            || fallbackResponse?.requestData?.image_type
                            || 'original'
                    };

                    if (fallbackOriginalImage.image_url) {
                        this.imageUrls.original = fallbackOriginalImage.image_url;
                    }

                    this.imageTypes.original = `image/${normalizeRelationFileType(fallbackOriginalImage.file_type || this.imageTypes.original)}`;
                    this.currentRelation = fallbackPayload?.original_image
                        ? fallbackPayload
                        : {
                            hash_id: hashId,
                            original_image: fallbackOriginalImage,
                            updated_at: new Date().toISOString()
                        };

                    return this.currentRelation;
                } catch (fallbackError) {
                    console.error('[ImageAssetStore] 原有逻辑也失败:', fallbackError);
                    throw fallbackError;
                }
            }
        },

        async hydrateOriginalImageFromServer(hashId) {
            const normalizedHashId = String(hashId || '').trim();
            if (!normalizedHashId) {
                return { success: false, reason: 'invalid_hash_id' };
            }

            const queryResult = await apiService.queryImageInfo(normalizedHashId, 'original');
            if (!queryResult?.exists || !queryResult.imageUrl) {
                return { success: false, reason: 'image_not_found' };
            }

            const fileType = normalizeRelationFileType(queryResult.data?.original_image?.file_type || 'jpeg');
            this.imageUrls.original = queryResult.imageUrl;
            this.imageIds.original = normalizedHashId;
            this.imageTypes.original = `image/${fileType}`;

            // 第一步：直接 CORS fetch（Chrome / Firefox 可用）
            try {
                const response = await fetch(withCorsCacheBust(queryResult.imageUrl), {
                    mode: 'cors',
                    credentials: 'omit',
                    cache: 'reload'
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const imageBlob = await response.blob();
                if (imageBlob.size === 0) {
                    throw new Error('Retrieved image blob is empty');
                }

                const base64Data = await this.blobToBase64(imageBlob);
                if (!base64Data) {
                    throw new Error('Failed to convert image to base64');
                }

                return {
                    success: true,
                    data: base64Data,
                    imageType: `image/${fileType}`,
                    imageUrl: queryResult.imageUrl,
                    fileType
                };
            } catch (corsError) {
                console.warn('[ImageAssetStore] CORS fetch 失败，尝试同源代理:', corsError?.message || corsError);
            }

            // 第二步：同源代理获取 base64（Safari 兜底）
            try {
                const proxyResult = await apiService.fetchImageViaProxy(normalizedHashId, {
                    imageUrl: queryResult.imageUrl,
                    fileType
                });

                if (proxyResult?.success && proxyResult.data) {
                    console.log('[ImageAssetStore] 同源代理获取图片成功:', {
                        size: proxyResult.data.length,
                        fileType: proxyResult.fileType
                    });
                    return {
                        success: true,
                        data: proxyResult.data,
                        imageType: proxyResult.imageType || `image/${fileType}`,
                        imageUrl: queryResult.imageUrl,
                        fileType: proxyResult.fileType || fileType,
                        source: 'proxy'
                    };
                }
            } catch (proxyError) {
                console.warn('[ImageAssetStore] 同源代理获取图片失败:', proxyError?.message || proxyError);
            }

            return { success: false, reason: 'cors_and_proxy_failed' };
        },

        applyRelation(normalizedRelation) {
            if (!normalizedRelation || typeof normalizedRelation !== 'object') {
                return null;
            }

            const original = normalizedRelation.original;
            if (original?.imageUrl) {
                this.imageUrls.original = original.imageUrl;
            }
            if (original?.fileType) {
                this.imageTypes.original = `image/${normalizeRelationFileType(original.fileType)}`;
            }
            if (original?.hashId) {
                this.imageIds.original = original.hashId;
            }

            const processed = normalizedRelation.processed;
            if (processed?.imageUrl) {
                this.imageUrls.processed = processed.imageUrl;
            }
            if (processed?.fileType) {
                this.imageTypes.processed = `image/${normalizeRelationFileType(processed.fileType)}`;
            }
            if (processed?.hashId) {
                this.imageIds.processed = processed.hashId;
            }

            const relationForStore = normalizedRelation.raw || {
                hash_id: normalizedRelation.hashId || original?.hashId || processed?.hashId || null,
                user_image_relation_id: normalizedRelation.relationId || null,
                updated_at: normalizedRelation.updatedAt || null,
                original_image: original
                    ? {
                        hash_id: original.hashId || null,
                        image_url: original.imageUrl || null,
                        file_type: normalizeRelationFileType(original.fileType),
                        image_type: original.imageType || 'original'
                    }
                    : null,
                processed_image: processed
                    ? {
                        hash_id: processed.hashId || null,
                        image_url: processed.imageUrl || null,
                        file_type: normalizeRelationFileType(processed.fileType),
                        image_type: processed.imageType || 'processed'
                    }
                    : null
            };

            this.currentRelation = relationForStore;
            return relationForStore;
        },

        async setProcessedImage(data, imageId) {
            this.processedImageData = data;
            if (data) {
                this.imageIds.processed = imageId;
                // 裁剪图的编码由 AnalysisProcessor 决定（当前固定 canvas.toDataURL('image/webp')），
                // 与「原图的格式」无关。这里必须以图片源的真实编码为准，否则 imageTypes.processed
                // 会把原图 MIME（如 image/jpeg）带进上传 file_type / 下载后缀 / 图片代理 mime，
                // 造成「文件名后缀与字节编码不符」。无法从源推断时才回退原图类型。
                this.imageTypes.processed = resolveImageMimeType(data, this.imageTypes.original)
                    || this.imageTypes.original;
            } else {
                this.imageIds.processed = null;
                this.imageTypes.processed = null;
            }
        },

        setOriginalFileInfo(fileInfo = {}) {
            this.originalFileInfo.format = fileInfo.format || '';
            this.originalFileInfo.name = fileInfo.name || '';
        },

        setFileObject(file) {
            this.fileObject = file;
        },

        async getLatestImage() {
            try {
                if (!this.db) {
                    await this.initDB();
                }
                const images = await this.db.getAll('images');
                images.sort((a, b) => b.id - a.id);

                if (images.length > 0) {
                    return images[0].data;
                }
                return null;
            } catch (error) {
                console.warn('[IndexedDB] getLatestImage failed:', error);
                return null;
            }
        },

        async getImageById(imageId) {
            try {
                if (!this.db) {
                    await this.initDB();
                }
                const imageData = await this.db.get('images', imageId);
                if (imageData) {
                    return {
                        data: imageData.data,
                        type: imageData.type,
                        timestamp: imageData.timestamp
                    };
                }
                return null;
            } catch (error) {
                console.warn('[IndexedDB] 获取图片数据失败:', error);
                return null;
            }
        },

        async cleanOldImages(maxAge = 7 * 24 * 60 * 60 * 1000) {
            try {
                if (!this.db) {
                    await this.initDB();
                }
                const now = Date.now();
                const images = await this.db.getAll('images');
                for (const image of images) {
                    if (now - image.timestamp > maxAge) {
                        await this.db.delete('images', image.id);
                    }
                }
            } catch (error) {
                console.warn('[IndexedDB] 清理过期数据失败:', error);
            }
        },

        async cacheImageData(imageId, base64Data, imageType) {
            if (!this.db) {
                await this.initDB();
            }

            try {
                const existingCache = await this.getImageById(imageId);
                if (existingCache?.data) {
                    return;
                }

                await this.db.put('images', {
                    id: imageId,
                    data: base64Data,
                    type: imageType || 'image/jpeg',
                    timestamp: Date.now()
                });
            } catch (error) {
                console.warn(`[ImageAssetStore] Failed to cache image ${imageId}:`, error);
            }
        },

        async bootstrapRelation(hashId, imageType = 'original', originalHashId = null, knownStatus = null) {
            try {
                this.relationStatus = 'loading';
                this.relationError = null;

                let fetchResult;
                if (knownStatus !== null) {
                    fetchResult = { status: knownStatus, payload: null, detail: null };
                } else {
                    fetchResult = await relationLegacyAdapter.fetchRelation(hashId, {
                        imageType,
                        originalHashId
                    });
                }

                switch (fetchResult.status) {
                    case 200:
                        this.currentRelation = fetchResult.payload;
                        this.relationStatus = 'success';
                        return fetchResult.payload;

                    case 403: {
                        if (!this.imageUrls[imageType]) {
                            throw new Error(`图片URL不存在: ${imageType}`);
                        }

                        const createResult = await relationLegacyAdapter.createOrUpdateRelation({
                            hash_id: hashId,
                            img_url: this.imageUrls[imageType],
                            file_type: (this.imageTypes[imageType] && this.imageTypes[imageType].split('/')[1]) || 'jpg',
                            image_type: imageType,
                            ...(imageType === 'processed' && originalHashId ? { original_hash_id: originalHashId } : {})
                        });

                        if (createResult.status === 200) {
                            this.currentRelation = createResult.payload;
                            this.relationStatus = 'success';
                            return createResult.payload;
                        }
                        throw new Error(`创建关系失败: ${createResult.detail?.error || '未知错误'}`);
                    }

                    case 410: {
                        const relationId = fetchResult.detail?.user_image_relation_id;
                        if (!relationId) {
                            throw new Error('无法恢复关系：缺少关系ID');
                        }

                        const restoreResult = await relationLegacyAdapter.restoreRelation(hashId, relationId);
                        if (restoreResult.status === 200) {
                            const touchResult = await relationLegacyAdapter.touchRelation(hashId, {
                                imageType,
                                originalHashId
                            });
                            if (touchResult.status === 200) {
                                this.currentRelation = touchResult.payload;
                                this.relationStatus = 'success';
                                return touchResult.payload;
                            }
                            throw new Error(`恢复后刷新失败: ${touchResult.detail?.error || '未知错误'}`);
                        }
                        throw new Error(`恢复关系失败: ${restoreResult.detail?.error || '未知错误'}`);
                    }

                    case 404: {
                        if (!this.imageData) {
                            throw new Error('缺少图片数据，无法上传');
                        }

                        // 上传源按角色区分：original = fileObject || imageData；processed = processedImageData。
                        // 旧实现 processed 误用 this.imageData（原图字节），会把它写入 processed 的 resource_key。
                        const savedSource = imageType === 'original'
                            ? (this.fileObject instanceof File ? this.fileObject : this.imageData)
                            : this.processedImageData;
                        if (!savedSource) {
                            throw new Error(`缺少上传源，无法保存 ${imageType} 图片`);
                        }

                        const saveOptions = imageType === 'processed'
                            ? {
                                originalHashId,
                                originalSource: this.fileObject instanceof File ? this.fileObject : this.imageData,
                                originalFileType: this.imageTypes.original
                            }
                            : {
                                uploadSource: this.fileObject instanceof File ? this.fileObject : this.imageData
                            };

                        // 复用批次上传服务（与 S5/S6 同一套），由 saveImageToServer 完成授权+PUT+POST /photos/。
                        // POST payload 不含 upload_id/resource_key；img_url 由后端 resource_key 拼公开基址生成。
                        const saveResult = await apiService.saveImageToServer(
                            hashId,
                            savedSource,
                            this.imageTypes[imageType],
                            imageType,
                            saveOptions
                        );

                        this.imageUrls[imageType] = saveResult.imageUrl;
                        this.currentRelation = saveResult.data;
                        this.relationStatus = 'success';
                        return saveResult.data;
                    }

                    case 409:
                        this.relationError = { type: 'processed_mismatch', detail: fetchResult.detail };
                        throw new Error('处理图与原图不匹配');

                    default:
                        throw new Error(`未知关系状态: ${fetchResult.status}`);
                }
            } catch (error) {
                this.relationStatus = 'error';
                this.relationError = error;
                throw error;
            }
        },

        async syncImageCache(hashId, imageUrl, blob = null) {
            try {
                if (!this.db) {
                    await this.initDB();
                }

                let imageData = blob;
                if (!imageData && imageUrl) {
                    try {
                        await new Promise((resolve) => setTimeout(resolve, 1000));
                        const response = await fetch(imageUrl, {
                            mode: 'cors',
                            credentials: 'omit'
                        });
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const imageBlob = await response.blob();
                        imageData = await this.blobToBase64(imageBlob);
                    } catch (error) {
                        console.error('[ImageAssetStore] 从URL获取图片数据失败:', error);
                        return;
                    }
                }

                if (imageData) {
                    await this.db.put('images', {
                        id: hashId,
                        data: imageData,
                        url: imageUrl,
                        timestamp: Date.now()
                    });
                }
            } catch (error) {
                console.warn(`[ImageAssetStore] 缓存同步失败: ${hashId}`, error);
            }
        },

        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        },

        resetRelation() {
            this.currentRelation = null;
            this.relationStatus = 'idle';
            this.relationError = null;
        },

        resetAssetState() {
            this.imageData = null;
            this.processedImageData = null;
            this.fileObject = null;
            this.imageUrls = createImageUrlsState();
            this.imageIds = createImageIdsState();
            this.imageTypes = createImageTypesState();
            this.originalFileInfo = createOriginalFileInfoState();
            this.resetRelation();
        }
    }
});
